import { appendLog } from './logStore.js';
import {
  getAppInfo,
  fetchUpdateManifest as fetchUpdateManifestFromApi,
} from './api/services/updaterService.js';

export async function getCurrentAppInfo() {
  return getAppInfo();
}

export async function fetchUpdateManifest(extraUrls = []) {
  return fetchUpdateManifestFromApi(extraUrls);
}

export function semverGt(a, b) {
  const pa = String(a || '0')
    .split('.')
    .map((x) => parseInt(x, 10) || 0);
  const pb = String(b || '0')
    .split('.')
    .map((x) => parseInt(x, 10) || 0);
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i++) {
    const va = pa[i] || 0,
      vb = pb[i] || 0;
    if (va > vb) return true;
    if (va < vb) return false;
  }
  return false;
}

export async function installIpk({ appId, ipkUrl, ipkPath }) {
  const svc = globalThis?.webOS?.service;
  if (!svc) throw new Error('webOS service unavailable');

  const installParams = { id: appId, subscribe: true };
  if (ipkUrl) installParams.ipkUrl = ipkUrl;
  if (ipkPath) installParams.ipkPath = ipkPath;

  return new Promise((resolve, reject) => {
    let finished = false;
    const done = (ok, payload) => {
      if (!finished) {
        finished = true;
        ok ? resolve(payload) : reject(payload);
      }
    };
    try {
      svc.request('luna://com.webos.appInstallService', {
        method: 'install',
        parameters: installParams,
        onSuccess: (res) => {
          const s = String(res?.status || res?.state || '').toLowerCase();
          appendLog({ type: 'info', detail: { event: 'update-progress', payload: res } });
          if (s === 'installed' || s === 'finished' || res?.returnValue === true) {
            done(true, res);
          }
        },
        onFailure: (err) => done(false, err),
      });
    } catch (e) {
      done(false, e);
    }
  });
}

export async function downloadViaDM(url, targetDir = 'file:///media/internal', fileName) {
  const svc = globalThis?.webOS?.service;
  if (!svc) return '';
  const name = fileName || url.split('/').pop() || 'app.ipk';
  return new Promise((resolve) => {
    try {
      svc.request('luna://com.webos.service.downloadmanager', {
        method: 'download',
        parameters: { target: url, targetDir, targetName: name, subscribe: true },
        onSuccess: (res) => {
          const s = String(res?.status || res?.state || '').toLowerCase();
          if (s === 'completed' || s === 'complete' || res?.completed || res?.finished) {
            resolve(res?.path || res?.filePath || res?.savePath || `${targetDir}/${name}`);
          }
        },
        onFailure: () => resolve(''),
      });
    } catch {
      resolve('');
    }
  });
}

export async function relaunchApp(appId) {
  const svc = globalThis?.webOS?.service;
  if (!svc) {
    try {
      location.reload();
    } catch {}
    return;
  }
  try {
    // Try close then launch for a clean restart
    await new Promise((r) => {
      try {
        svc.request('luna://com.webos.service.applicationmanager', {
          method: 'close',
          parameters: { id: appId },
          onSuccess: () => r(),
          onFailure: () => r(),
        });
      } catch {
        r();
      }
    });
  } catch {}
  try {
    svc.request('luna://com.webos.service.applicationmanager', {
      method: 'launch',
      parameters: { id: appId },
    });
  } catch {
    try {
      location.reload();
    } catch {}
  }
}

export async function checkAndUpdate({ updateUrls = [], auto = true } = {}) {
  const { id: appId, version: current } = await getCurrentAppInfo();
  const manifest = await fetchUpdateManifest(updateUrls);
  if (!manifest) return { updated: false, reason: 'no-manifest' };
  const remoteVer = String(manifest.version || '0.0.0');
  if (!semverGt(remoteVer, current))
    return { updated: false, reason: 'up-to-date', current, remoteVer };
  const ipkUrl = manifest.url || manifest.ipkUrl;
  if (!auto) return { updated: false, reason: 'available', current, remoteVer, ipkUrl };

  appendLog({ type: 'info', detail: { event: 'update-start', current, remoteVer, ipkUrl } });

  try {
    // Prefer direct install via URL
    try {
      await installIpk({ appId, ipkUrl });
    } catch {
      // Fallback: download then install via path
      const path = await downloadViaDM(ipkUrl, 'file:///media/internal');
      if (!path) throw new Error('download-failed');
      await installIpk({ appId, ipkPath: path });
    }
    appendLog({ type: 'info', detail: { event: 'update-installed', current, remoteVer } });
    await relaunchApp(appId);
    return { updated: true, current, remoteVer };
  } catch (e) {
    appendLog({
      type: 'error',
      detail: { event: 'update-failed', message: String(e?.message || e) },
    });
    return { updated: false, reason: 'install-failed', error: String(e?.message || e) };
  }
}
